import { useB2BTranslation } from '../hooks/useB2BTranslation';
import { B2B_FORM_VALIDATION } from '../constants';

/**
 * Centralized B2B validation rules with translation support
 * Replaces all duplicate validation patterns across components
 */

export interface B2BValidationRule {
  required?: boolean | string;
  minLength?: { value: number; message?: string };
  maxLength?: { value: number; message?: string };
  pattern?: { value: RegExp; message?: string };
  validate?: (value: any, formValues?: any) => boolean | string;
}

export interface B2BValidationRules {
  [fieldName: string]: B2BValidationRule;
}

/**
 * Creates validation rules for common B2B form fields
 * Uses translation keys for consistent error messages
 */
export const createB2BValidationRules = (t: (key: string) => string) => ({
  // Personal Information
  name: {
    required: t('validation.nameRequired'),
    minLength: { 
      value: B2B_FORM_VALIDATION.NAME_MIN_LENGTH, 
      message: t('validation.nameMinLength') 
    },
    maxLength: { 
      value: B2B_FORM_VALIDATION.NAME_MAX_LENGTH, 
      message: t('validation.nameMaxLength') 
    },
    pattern: {
      value: /^[a-zA-Z\s\-'.]+$/,
      message: t('validation.namePattern')
    }
  },

  surname: {
    required: t('validation.surnameRequired'),
    minLength: { 
      value: B2B_FORM_VALIDATION.NAME_MIN_LENGTH, 
      message: t('validation.surnameMinLength') 
    },
    maxLength: { 
      value: B2B_FORM_VALIDATION.NAME_MAX_LENGTH, 
      message: t('validation.surnameMaxLength') 
    },
    pattern: {
      value: /^[a-zA-Z\s\-'.]+$/,
      message: t('validation.surnamePattern')
    }
  },

  email: {
    required: t('validation.emailRequired'),
    pattern: {
      value: B2B_FORM_VALIDATION.EMAIL_PATTERN,
      message: t('validation.emailPattern')
    }
  },

  phoneNumber: {
    required: t('validation.phoneRequired'),
    // Remove minLength and maxLength - they check the entire string including country code
    // We handle length validation in the validate function below
    validate: (value: any) => {
      if (!value || typeof value !== 'string') {
        return t('validation.phoneRequired');
      }
      
      // Phone number is stored as "countryCode phoneNumber" (e.g., "+44 020674536")
      // We need to validate only the phone number part (after the space)
      const parts = value.trim().split(' ');
      const phoneNumberPart = parts.length > 1 ? parts.slice(1).join(' ') : parts[0];
      
      // Remove all non-digit characters to count actual digits
      const digitsOnly = phoneNumberPart.replace(/\D/g, '');
      
      // Check minimum digits
      if (digitsOnly.length < 7) {
        return t('validation.phoneMinLength');
      }
      
      // Check maximum digits
      if (digitsOnly.length > 15) {
        return t('validation.phoneMaxLength');
      }
      
      // Check pattern - allow digits, hyphens, spaces, and parentheses
      if (!/^[0-9\-\s()]+$/.test(phoneNumberPart)) {
        return t('validation.phonePattern');
      }
      
      return true;
    }
  },

  countryCode: {
    required: t('validation.countryCodeRequired')
  },

  // Authentication
  password: {
    required: t('validation.passwordRequired'),
    minLength: { 
      value: B2B_FORM_VALIDATION.PASSWORD_MIN_LENGTH, 
      message: t('validation.passwordMinLength') 
    },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      message: t('validation.passwordComplexity')
    }
  },

  confirmPassword: {
    required: t('validation.confirmPasswordRequired'),
    validate: (value: any, formValues?: any) => 
      value === formValues?.password || t('validation.passwordMismatch')
  },

  // Business Information
  businessName: {
    required: t('validation.businessNameRequired'),
    minLength: { 
      value: 2, 
      message: t('validation.businessNameMinLength') 
    },
    maxLength: { 
      value: 100, 
      message: t('validation.businessNameMaxLength') 
    }
  },

  businessWebsite: {
    pattern: {
      value: B2B_FORM_VALIDATION.URL_PATTERN,
      message: t('validation.websitePattern')
    }
  },

  businessDescription: {
    maxLength: { 
      value: 500, 
      message: t('validation.businessDescriptionMaxLength') 
    }
  },

  // Profile Information
  position: {
    required: t('validation.positionRequired')
  },

  customPosition: {
    validate: (value: any, formValues?: any) => {
      const posIncludesOther = Array.isArray(formValues?.position)
        ? formValues.position.includes("other")
        : formValues?.position === "other";
      if (posIncludesOther && (!value || !value.trim())) {
        return t('validation.customPositionRequired');
      }
      return true;
    }
  },

  linkedinUrl: {
    validate: (value: any, formValues?: any) => {
      if (formValues?.linkedin === "custom" && value && value.trim()) {
        const linkedinPattern = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
        return linkedinPattern.test(value) || t('validation.linkedinPattern');
      }
      return true;
    }
  },

  interests: {
    validate: (value: any) => {
      if (!value || value.length === 0) {
        return t('validation.interestsRequired');
      }
      return true;
    }
  },

  // Admin fields
  adminNotes: {
    maxLength: { 
      value: 1000, 
      message: t('validation.adminNotesMaxLength') 
    }
  },

  // Terms and consent
  termsAccepted: {
    required: t('validation.termsRequired'),
    validate: (value: any) => value === true || t('validation.termsRequired')
  },

  gdprConsent: {
    required: t('validation.gdprConsentRequired'),
    validate: (value: any) => value === true || t('validation.gdprConsentRequired')
  },

  marketingConsent: {
    required: false // Optional field
  }
});

/**
 * Hook to get validation rules with translation support
 */
export const useB2BValidationRules = () => {
  const { t } = useB2BTranslation();
  return createB2BValidationRules(t);
};

/**
 * Pre-configured validation rule sets for common forms
 */
export const B2B_VALIDATION_SETS = {
  // Registration form validation
  REGISTRATION: [
    'name', 'surname', 'email', 'countryCode', 'phoneNumber', 
    'password', 'confirmPassword', 'termsAccepted', 'gdprConsent', 'marketingConsent'
  ],
  
  // Profile creation validation
  PROFILE_CREATE: [
    'name', 'surname', 'email', 'phoneNumber', 'position', 
    'customPosition', 'linkedinUrl', 'interests'
  ],
  
  // Business information validation
  BUSINESS_INFO: [
    'businessName', 'businessWebsite', 'businessDescription'
  ],
  
  // Admin profile validation
  ADMIN_PROFILE: [
    'name', 'surname', 'email', 'phoneNumber', 'position', 
    'customPosition', 'linkedinUrl', 'interests', 'adminNotes'
  ]
} as const;

/**
 * Get validation rules for a specific form type
 */
export const getValidationRulesForForm = (formType: keyof typeof B2B_VALIDATION_SETS, t: (key: string) => string) => {
  const allRules = createB2BValidationRules(t);
  const fields = B2B_VALIDATION_SETS[formType];
  
  const rules: B2BValidationRules = {};
  fields.forEach(field => {
    if (allRules[field]) {
      rules[field] = allRules[field];
    }
  });
  
  return rules;
};
